import {$isDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import type {Column} from 'components/DSYS/Table';
import type {LexicalEditor, NodeKey} from 'lexical';
import {$getNodeByKey} from 'lexical';
import {useCallback, useMemo} from 'react';

import {getColumnWidthsFromValue, valueWithUpdatedColumnWidths} from './columnWidthPersistence';
import {getTableWidthPx} from './getTableWidthPx';

/**
 * Merges persisted `value.columnWidths` into `baseColumns`, sums width for breakout layout,
 * and wires `onColumnResizeEnd` to persist back onto the dynamic node.
 */
export function useColumnResizePersistence<T>({
  editor,
  nodeKey,
  setValue,
  baseColumns,
  value,
  enabled = true,
  allowResize = enabled,
}: {
  editor: LexicalEditor;
  nodeKey: NodeKey;
  setValue: (next: unknown) => void;
  baseColumns: Column<T>[];
  value: unknown;
  /** When false, use `baseColumns` only and do not attach a resize handler. */
  enabled?: boolean;
  /** When false, still merge widths if `enabled`, but omit `onColumnResizeEnd` (e.g. read-only editor). */
  allowResize?: boolean;
}): {
  columns: Column<T>[];
  tableWidthPx: number;
  onColumnResizeEnd: ((columnType: string, width: number) => void) | undefined;
} {
  const columns = useMemo((): Column<T>[] => {
    if (!enabled) {
      return baseColumns;
    }
    const saved = getColumnWidthsFromValue(value);
    return baseColumns.map((col) => {
      const w = saved[col.type];
      if (typeof w === 'number' && Number.isFinite(w)) {
        return {...col, width: w};
      }
      return col;
    });
  }, [baseColumns, enabled, value]);

  const tableWidthPx = useMemo(() => getTableWidthPx(columns), [columns]);

  const handleColumnResizeEnd = useCallback(
    (columnType: string, width: number) => {
      if (!enabled || !allowResize || !editor.isEditable()) {
        return;
      }
      let nextValue: unknown;
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isDynamicNode(node)) {
          return;
        }
        const cur = node.getValue();
        const widths = {...getColumnWidthsFromValue(cur), [columnType]: width};
        nextValue = valueWithUpdatedColumnWidths(cur, widths);
      });
      if (nextValue !== undefined) {
        setValue(nextValue);
      }
    },
    [allowResize, enabled, editor, nodeKey, setValue]
  );

  return {
    columns,
    tableWidthPx,
    onColumnResizeEnd: enabled && allowResize ? handleColumnResizeEnd : undefined,
  };
}
